"use client";

import Image from "next/image";
import Link from "next/link";
import { ReactNode, useEffect } from "react";

import WithdrawalFooter from "@/components/organisms/withdrawal-page/withdrawal-footer";
import { MAIN_PAGE_URL } from "@/constants";
import { storageAvailable } from "@/utils/common.utils";

interface FooterContent {
  termsAndConditions?: string;
  termsOfBusiness?: string;
  privacyPolicy?: string;
  cookiePolicy?: string;
  accessibility?: string;
  copyrightText?: string;
}

interface PolicyActionLayoutProps {
  children: ReactNode;
  showFooter?: boolean;
  footerContent?: FooterContent;
  logoLinkUrl?: string;
  helpButton?: ReactNode;
}

export default function PolicyActionLayout({
  children,
  showFooter = true,
  footerContent,
  logoLinkUrl,
  helpButton,
}: Readonly<PolicyActionLayoutProps>) {
  useEffect(() => {
    if (typeof window !== "undefined" && window) {
      if (storageAvailable()) {
        sessionStorage.setItem("footer", "true");
      }
    }
  }, []);

  return (
    <div
      data-testid="policy-action-layout"
      className="bg-surface-neutral-option-2 min-h-[100dvh] flex flex-col"
    >
      <nav
        className="bg-surface-neutral-option-1 border-b border-border-neutral-subtle"
        data-testid="policy-action-navigation"
      >
        <div className="flex justify-between items-center py-spacing-s px-spacing-l md:py-spacing-m md:px-spacing-xxl lg:py-[20px] lg:px-spacing-l">
          <Link
            href={logoLinkUrl ?? MAIN_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid="policy-action-logo-link"
          >
            <Image
              src="/images/logos/staysure-logo-primary.svg"
              alt="Staysure"
              width={198}
              height={48}
              className="w-[138px] h-[30px] lg:w-[198px] lg:h-[48px]"
              priority
            />
          </Link>

          {helpButton}
        </div>
      </nav>

      <main
        className="flex-1 bg-surface-neutral-option-2"
        data-testid="policy-action-layout"
      >
        {children}
      </main>

      {showFooter && (
        <WithdrawalFooter
          termsAndConditions={footerContent?.termsAndConditions}
          termsOfBusiness={footerContent?.termsOfBusiness}
          privacyPolicy={footerContent?.privacyPolicy}
          cookiePolicy={footerContent?.cookiePolicy}
          accessibility={footerContent?.accessibility}
          copyrightText={footerContent?.copyrightText}
        />
      )}
    </div>
  );
}
